import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import type { Project } from '../../shared/types';
import { stageLabels } from '../lib/format';

export function ProjectPicker({ projects, value, onChange }: { projects: Project[]; value: string; onChange: (slug: string) => void }) {
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const options = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const selectedIndex = Math.max(0, projects.findIndex(p => p.slug === value));
  const selected = projects[selectedIndex];
  const current = Math.min(active, Math.max(0, projects.length - 1));
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => { if (!root.current?.contains(event.target as Node)) setOpen(false); };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  useEffect(() => {
    if (open) options.current?.children[current]?.scrollIntoView?.({ block: 'nearest' });
  }, [current, open]);
  const show = () => { setActive(selectedIndex); setOpen(true); typed.current = { text: '', at: 0 }; };
  const choose = (index: number) => { if (projects[index]) onChange(projects[index].slug); setOpen(false); trigger.current?.focus(); };
  const keyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const { key } = event;
    if (key === 'Tab') { setOpen(false); return; }
    if (key === 'Escape') { if (open) { event.preventDefault(); event.stopPropagation(); setOpen(false); } return; }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) {
      event.preventDefault();
      if (key === 'Enter' || key === ' ') { if (open) choose(current); else show(); return; }
      if (!open) { show(); if (key === 'Home') setActive(0); if (key === 'End') setActive(projects.length - 1); return; }
      setActive(key === 'Home' ? 0 : key === 'End' ? projects.length - 1 : (current + (key === 'ArrowDown' ? 1 : -1) + projects.length) % projects.length);
      return;
    }
    if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      const now = Date.now();
      const text = now - typed.current.at < 700 ? typed.current.text + key : key;
      typed.current = { text: text.toLowerCase(), at: now };
      const match = projects.findIndex(p => p.name.toLowerCase().startsWith(typed.current.text));
      if (!open) setOpen(true);
      if (match >= 0) setActive(match);
    }
  };
  return <div className="project-picker" ref={root} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}>
    <span className="project-picker-label" id={`${id}-label`}>Project in focus</span>
    <button type="button" ref={trigger} role="combobox" aria-labelledby={`${id}-label ${id}-value`} aria-expanded={open} aria-haspopup="listbox" aria-controls={open ? `${id}-list` : undefined} aria-activedescendant={open ? `${id}-option-${current}` : undefined} className="project-picker-trigger" onClick={() => open ? setOpen(false) : show()} onKeyDown={keyDown}>
      <span className={`picker-dot accent-${selected?.accent ?? 'cyan'}`}/><span id={`${id}-value`}>{selected?.name ?? 'Choose a project'}</span><ChevronDown size={16} className={open ? 'is-open' : ''}/>
    </button>
    {open && <div className="project-picker-popup"><div className="picker-hint mono">CHOOSE YOUR FOCUS</div><div ref={options} id={`${id}-list`} role="listbox" aria-labelledby={`${id}-label`} className="project-picker-options">
      {projects.map((project,index) => <div key={project.id} id={`${id}-option-${index}`} role="option" aria-selected={project.slug === value} className={`project-picker-option ${index === current ? 'is-active' : ''}`} onPointerMove={() => setActive(index)} onMouseDown={e => e.preventDefault()} onClick={() => choose(index)}>
        <span className={`picker-dot accent-${project.accent}`}/><span><strong>{project.name}</strong><small>{stageLabels[project.stage]}</small></span>{project.slug === value && <Check size={16} aria-hidden="true"/>}
      </div>)}
    </div></div>}
  </div>;
}
